/**
 * ErrorBoundary — the app had NONE, so a single render throw in any page
 * unmounted the whole tree → blank white screen on "almost every page" (a page's
 * real-data crash took down the shell too). This contains a crash to its own
 * boundary and SHOWS the error message, so:
 *   - page-level: the sidebar/shell survive, only the broken page shows a card,
 *     and navigating away (key={pathname}) auto-recovers.
 *   - app-level: a last-resort full-screen card instead of a blank page.
 *
 * The error message is surfaced on purpose (it is a JS runtime string, not a
 * secret) so the cause is visible in prod without opening devtools.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  level?: 'app' | 'page';
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface in the console for source-mapped stacks during diagnosis.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isApp = this.props.level === 'app';
    const card = (
      <div className="mx-auto max-w-lg rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">หน้านี้เกิดข้อผิดพลาด</h2>
        <p className="mt-1 text-sm text-gray-500">
          ระบบเปิดหน้านี้ไม่สำเร็จ ลองโหลดใหม่ หรือกลับหน้าหลัก เมนูอื่นยังใช้งานได้ตามปกติ
        </p>
        <pre className="mt-3 overflow-auto rounded-lg bg-gray-50 p-3 text-left text-xs text-red-700 max-h-40 whitespace-pre-wrap break-words">
          {error.message || String(error)}
        </pre>
        <div className="mt-4 flex justify-center gap-2">
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            โหลดใหม่
          </button>
          <a
            href="/"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            กลับหน้าหลัก
          </a>
        </div>
      </div>
    );

    if (isApp) {
      return <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">{card}</main>;
    }
    return <div className="py-10">{card}</div>;
  }
}
